<template>
  <div class="modern-color-theme font-poppins flex flex-col" :class="theme.container" data-component-name="VNumber">
    <VSLabel v-if="props.label" :tooltip="props.labelTooltip" :for="id" :theme="theme.__key">{{ props.label }}</VSLabel>
    <div
      class="flex items-center ring-1 ring-inset"
      :class="[
        computedWrapperClass,
        computedContentClass,
        {
          'focus-within:ring-2 focus-within:ring-inset focus-within:ring-blue-600': theme.focus
        }
      ]"
    >
      <div v-if="slots.prefix" class="pl-2">
        <slot name="prefix" />
      </div>
      <input
        :id="id"
        ref="focusElement"
        v-model="modelValueInternalRaw"
        type="text"
        :disabled="props.disabled"
        :readonly="props.readonly"
        :placeholder="props.placeholder"
        :maxlength="props.maxLength"
        :max="props.max"
        :min="props.min"
        :step="props.step"
        :name="props.name"
        :autocomplete="props.autocomplete"
        class="w-full m-[2px] pl-2.5 !border-none focus:ring-0 !shadow-none py-[calc(0.375rem-2px)] placeholder:text-neutral-500 text-sm leading-6"
        :class="[computedInputClass, computedContentClass]"
        :aria-describedby="descriptionId"
        @change="handleChange"
        @keydown="(event) => { handleKeyDown(event); startFlushTimer() }"
        @paste="handlePaste"
        @keydown.enter="flush"
        @blur="flush"
      >
      <div v-if="props.maxLength" class="pr-4 whitespace-nowrap text-xs leading-4 font-normal" :class="{ 'text-neutral-500': !props.disabled, 'text-neutral-250': props.disabled }" role="status" aria-live="polite">
        {{ modelValueInternalLength }} / {{ props.maxLength }}
      </div>
      <VSResetButton v-if="!isNullOrUndefined(modelValue)" container="ml-auto pr-2 flex" v-bind="props" @click="reset" />
      <div v-if="slots.suffix" class="pr-2 flex items-center gap-2">
        <slot name="suffix" />
      </div>
    </div>
    <VSValidation v-if="validationResult" :type="validationResult[0]" :message="validationResult[1]" />
    <VSDescription v-if="props.description" :id="descriptionId" v-safe-html="props.description" :theme="theme.__key" />
  </div>
</template>
<script lang="ts" setup>
import { computed, ref, useSlots, watch } from 'vue';
import { isControlCharacter, isNullOrUndefined, isNumberLikeCharacter } from '../../utils/validations';
import VSDescription from './components/VSDescription.vue'
import VSLabel from './components/VSLabel.vue'
import { useElementId, useDescriptionElementId } from '../../utils/utils';
import { useFocus } from '@component-utils/focus';
import { stopAndPrevent } from '~/features/_abstract/utils/event';
import { Key } from '@avvoka/shared';
import { SHARED_PROPS_DEFAULTS, VALIDATION_PROPS_DEFAULTS, type AutocompleteProps, type BufferingProps, type LengthRestrictionProps, type PlaceholderProps, type SharedProps, type ValidationProps } from './helpers/types';
import VSValidation from './components/VSValidation.vue';
import { createDefaultNumberValidator, useValidation } from './helpers/validations';
import VSResetButton from './components/VSResetButton.vue';
import type { ThemeProp } from '@component-library/themes/types';
import { useTheme } from '@component-library/themes/themes';
import { SCHEDULE_FLUSH_MS, useFlushTimer } from './helpers/buffering';
import { parseNumber, stringifyNumber } from './helpers/numbers';

defineOptions({
  name: 'VNumber'
})

const slots = useSlots()

const props = withDefaults(
  defineProps<SharedProps & ValidationProps<number> & AutocompleteProps & LengthRestrictionProps & PlaceholderProps & BufferingProps & {
    focus?: boolean
    step?: number
    min?: number
    max?: number
    centered?: boolean
    required?: boolean
    allowEmpty?: boolean
    theme?: ThemeProp<'VNumber'>
  }>(),
  {
    ...SHARED_PROPS_DEFAULTS,
    ...VALIDATION_PROPS_DEFAULTS,
    focus: false,
    min: undefined,
    max: undefined,
    step: undefined,
    required: undefined,
    placeholder: undefined,
    autocomplete: 'off',
    centered: false,
    allowEmpty: false,
    theme: undefined
  }
)

const id = useElementId(props.id)
const descriptionId = useDescriptionElementId(props.description, id.value)

const theme = useTheme(props, 'VNumber')

const modelValue = defineModel<undefined | null | number>({ required: true })

const precision = computed(() => Math.pow(10, String(props.step ?? 1).split('.')[1]?.length ?? 0))
watch(precision, (newValue, oldValue) => {
  if (modelValueInternal.value) {
    modelValueInternal.value = modelValueInternal.value / oldValue * newValue
  }
})

const modelValueInternal = ref(!isNullOrUndefined(modelValue.value) ? Math.trunc((modelValue.value ?? props.min ?? 0) * precision.value) : null)
const modelValueInternalRaw = ref(stringifyNumber(modelValue.value ?? ''))

watch(modelValue, (value) => {
  modelValueInternalRaw.value = stringifyNumber(value ?? '')
  modelValueInternal.value = !isNullOrUndefined(value) ? Math.trunc((value ?? props.min ?? 0) * precision.value) : null
})

watch(modelValueInternal, (value) => {
  if (!props.buffered) modelValue.value = isNullOrUndefined(value) ? value : (value / precision.value)
})

const modelValueInternalLength = computed(() => modelValueInternalRaw.value.length)

const updateModelValue = () => {
  const value = modelValueInternal.value
  if (isNullOrUndefined(value)) {
    modelValueInternalRaw.value = ''
    modelValue.value = null
  } else {
    modelValueInternalRaw.value = stringifyNumber(modelValue.value = value / precision.value)
  }
}

const handleChange = () => {
  const rawValue = modelValueInternalRaw.value

  const isEmpty = rawValue === '' || rawValue === '-'

  const unscaledValue = isEmpty ? (props.min ?? 0) : parseNumber(rawValue)
  let value = typeof props.step === 'number' ? Math.trunc(unscaledValue * precision.value) : unscaledValue

  if (typeof props.max === 'number' && value > props.max * precision.value) {
    value = props.max * precision.value
  }

  if (typeof props.min === 'number' && value < props.min * precision.value) {
    value = props.min * precision.value
  }

  if (isEmpty) {
    modelValueInternal.value = null
  } else {
    modelValueInternal.value = value
    modelValueInternalRaw.value = stringifyNumber(value / precision.value)
  }
}

const { startFlushTimer, abortFlushTimer } = useFlushTimer(() => {
  handleChange()

  if (props.buffered) {
    updateModelValue()
  }
}, typeof props.buffered === 'object' ? props.buffered.delay ?? SCHEDULE_FLUSH_MS : SCHEDULE_FLUSH_MS)

const flush = () => {
  if (props.buffered) {
    abortFlushTimer()

    updateModelValue()
  }
}

const reset = () => {
  abortFlushTimer()

  modelValue.value = null
  modelValueInternalRaw.value = ''
  modelValueInternal.value = null
}

const modelValueForValidation = computed(() => isNullOrUndefined(modelValueInternal.value) ? null : modelValueInternal.value / precision.value)

const { validationVisible, validationResult, isValid, isValidationError, isValidationWarning } = useValidation<number>(
  modelValueForValidation,
  props,
  createDefaultNumberValidator(props)
)

const computedContentClass = computed(() => ({
  [theme.value.content.base]: true,
  [theme.value.content.disabled]: props.disabled,
  [theme.value.content.readonly]: !props.disabled && props.readonly,
  [theme.value.content.enabled]: !props.disabled && !props.readonly
}))

const computedInputClass = computed(() => ({
  'text-center': props.centered,
}))

const computedWrapperClass = computed(() => ({
  [theme.value.wrapper.disabled]: props.disabled,
  [theme.value.wrapper.readonly]: !props.disabled && props.readonly,
  [theme.value.wrapper.enabled]: !props.disabled && !props.readonly,
  'ring-red-300': isValidationError.value,
  'ring-yellow-300': isValidationWarning.value
}))

const { focus, focusElement } = useFocus(props.focus)

const handlePaste = (event: ClipboardEvent) => {
  stopAndPrevent(event)

  if (props.readonly || props.disabled) return

  const clipboardData = event.clipboardData
  if (!clipboardData) return

  const value = parseNumber(clipboardData.getData('text/plain') ?? '')
  if (!Number.isNaN(value)) {
    const stringifiedValue = modelValueInternalRaw.value = stringifyNumber(value).slice(0, props.maxLength ?? Infinity)

    const element = focusElement.value as HTMLInputElement
    element.selectionDirection = 'none'
    element.selectionStart = stringifiedValue.length
    element.selectionEnd = stringifiedValue.length

    handleChange()

    flush()
  }
}

const handleKeyDown = (event: KeyboardEvent) => {
  validationVisible.value = true

  const element = focusElement.value as HTMLInputElement
  const key = event.key

  if (key === '-') {
    // Disallow typing of 'minus' when min is equal or above 0
    if (typeof props.min === 'number' && props.min >= 0) {
      return stopAndPrevent(event)
    }

    const isAtLeastFirstCharacterSelected = element.selectionStart === 0 && (element.selectionEnd && element.selectionEnd > 0)
    
    // Disallow typing of 'minus' outside of first position or if number is already negative
    if (!isAtLeastFirstCharacterSelected && (element.selectionStart !== 0 || element.value[0] === '-')) {
      return stopAndPrevent(event)
    }
  } else if (key === '.') {
    // If step is 1 or above, disallow typing dot
    if (typeof props.step === 'number' && precision.value <= 1) {
      return stopAndPrevent(event)
    }

    // Disallow typing of 'dot' if there is already a dot present, allow if existing dot is selected
    const dotPosition = element.value.indexOf('.')
    const minusPosition = element.value.indexOf('-')

    if (dotPosition !== -1) {
      const selStart = element.selectionStart
      const selEnd = element.selectionEnd

      if (!selStart || !selEnd || selStart > dotPosition || selEnd < dotPosition) {
        return stopAndPrevent(event)
      }
    }

    if (minusPosition !== -1) {
      const selStart = element.selectionStart
      const selEnd = element.selectionEnd

      if (selStart === 0 && (!selEnd || selStart === selEnd)) {
        return stopAndPrevent(event)
      }
    }
  }

  if (event.ctrlKey || event.altKey || event.metaKey) {
    return
  }

  if (isNumberLikeCharacter(key)) {
    // Let them type
  } else if (isControlCharacter(key as Key)) {
    // Handle arrow up and down
    if (key === Key.ArrowUp || key === Key.ArrowDown) {
      stopAndPrevent(event)

      const modificator = key === Key.ArrowUp ? +1 : -1

      const value = (modelValueInternal.value ?? ((props.min ?? 0) * precision.value)) + modificator * (props.step ?? 1) * precision.value

      modelValueInternalRaw.value = stringifyNumber(value / precision.value)

      handleChange()
    }
  } else {
    stopAndPrevent(event)
  }
}

defineExpose({
  focus,
  isValid,
  get isEmpty () {
    return isNullOrUndefined(modelValueInternal.value)
  }
})
</script>